import React from 'react';
import { X, Code, FileJson, Server, Bell } from 'lucide-react';

interface ApiDocumentationProps {
  onClose: () => void;
}

export const ApiDocumentation: React.FC<ApiDocumentationProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Code className="w-6 h-6 text-blue-600 shrink-0" />
            <span className="break-words">API-Dokumentation</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-8">
          {/* Introduction */}
          <section>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Einführung</h3>
            <p className="text-gray-600 mb-4">
              Die InstallTrack Pro API ermöglicht es Ihnen, programmatisch auf Ihre Daten zuzugreifen und mit der Anwendung zu interagieren.
              Diese Dokumentation beschreibt die verfügbaren Endpunkte und wie Sie diese verwenden können.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-blue-800 mb-2">Basis-URL</h4>
              <code className="block bg-blue-100 p-2 rounded text-blue-800 font-mono">
                https://api.installtrack.pro/v1
              </code>
              <p className="text-sm text-blue-700 mt-2">
                Alle API-Anfragen müssen an diese Basis-URL gesendet werden.
              </p>
            </div>
          </section>

          {/* Authentication */}
          <section>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Authentifizierung</h3>
            <p className="text-gray-600 mb-4">
              Alle API-Anfragen erfordern einen API-Schlüssel. Der Schlüssel muss im <code className="bg-gray-100 px-1 py-0.5 rounded">Authorization</code>-Header übergeben werden.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-800 mb-2">Beispiel</h4>
              <code className="block bg-gray-100 p-2 rounded text-gray-800 font-mono">
                Authorization: Bearer itpro_your_api_key
              </code>
            </div>
          </section>

          {/* Endpoints */}
          <section>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Endpunkte</h3>
            
            {/* Buildings */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <FileJson className="w-5 h-5 text-blue-600" />
                <h4 className="text-lg font-semibold text-gray-800">Gebäude</h4>
              </div>
              
              <div className="space-y-4">
                {/* GET /buildings */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs font-bold">GET</span>
                      <code className="font-mono text-sm">/buildings</code>
                    </div>
                    <span className="text-sm text-gray-500">Alle Gebäude abrufen</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Gibt eine Liste aller Gebäude zurück.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Beispielgebäude",
      "address": "Musterstraße 1, 12345 Musterstadt",
      "floors": [...]
    },
    ...
  ],
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>

                {/* POST /buildings */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs font-bold">POST</span>
                      <code className="font-mono text-sm">/buildings</code>
                    </div>
                    <span className="text-sm text-gray-500">Neues Gebäude erstellen</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Erstellt ein neues Gebäude.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Anfrage</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "name": "Neues Gebäude",
  "address": "Beispielstraße 123, 10115 Berlin"
}`}
                    </pre>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Neues Gebäude",
    "address": "Beispielstraße 123, 10115 Berlin",
    "floors": []
  },
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>

                {/* GET /buildings/{id} */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs font-bold">GET</span>
                      <code className="font-mono text-sm">/buildings/{'{id}'}</code>
                    </div>
                    <span className="text-sm text-gray-500">Gebäude abrufen</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Gibt ein bestimmtes Gebäude zurück.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Parameter</h5>
                    <ul className="list-disc list-inside text-sm text-gray-600 mb-2">
                      <li><code className="bg-gray-100 px-1 py-0.5 rounded">id</code> - Die ID des Gebäudes</li>
                    </ul>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Beispielgebäude",
    "address": "Musterstraße 1, 12345 Musterstadt",
    "floors": [...]
  },
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>

                {/* PUT /buildings/{id} */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-xs font-bold">PUT</span>
                      <code className="font-mono text-sm">/buildings/{'{id}'}</code>
                    </div>
                    <span className="text-sm text-gray-500">Gebäude aktualisieren</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Aktualisiert ein bestehendes Gebäude.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Parameter</h5>
                    <ul className="list-disc list-inside text-sm text-gray-600 mb-2">
                      <li><code className="bg-gray-100 px-1 py-0.5 rounded">id</code> - Die ID des Gebäudes</li>
                    </ul>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Anfrage</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "name": "Aktualisiertes Gebäude",
  "address": "Neue Adresse 456, 10115 Berlin"
}`}
                    </pre>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Aktualisiertes Gebäude",
    "address": "Neue Adresse 456, 10115 Berlin",
    "floors": [...]
  },
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>

                {/* DELETE /buildings/{id} */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-xs font-bold">DELETE</span>
                      <code className="font-mono text-sm">/buildings/{'{id}'}</code>
                    </div>
                    <span className="text-sm text-gray-500">Gebäude löschen</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Löscht ein Gebäude.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Parameter</h5>
                    <ul className="list-disc list-inside text-sm text-gray-600 mb-2">
                      <li><code className="bg-gray-100 px-1 py-0.5 rounded">id</code> - Die ID des Gebäudes</li>
                    </ul>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000"
  },
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Apartments */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <FileJson className="w-5 h-5 text-blue-600" />
                <h4 className="text-lg font-semibold text-gray-800">Wohnungen</h4>
              </div>
              
              <div className="space-y-4">
                {/* PUT /buildings/{buildingId}/apartments/{apartmentId} */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-xs font-bold">PUT</span>
                      <code className="font-mono text-sm">/buildings/{'{buildingId}'}/apartments/{'{apartmentId}'}</code>
                    </div>
                    <span className="text-sm text-gray-500">Wohnung aktualisieren</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Aktualisiert eine bestehende Wohnung.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Parameter</h5>
                    <ul className="list-disc list-inside text-sm text-gray-600 mb-2">
                      <li><code className="bg-gray-100 px-1 py-0.5 rounded">buildingId</code> - Die ID des Gebäudes</li>
                      <li><code className="bg-gray-100 px-1 py-0.5 rounded">apartmentId</code> - Die ID der Wohnung</li>
                    </ul>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Anfrage</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "status": "completed",
  "notes": "Installation erfolgreich abgeschlossen",
  "technician": "Max Mustermann",
  "konzernId": "KID-12345"
}`}
                    </pre>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": {
    "id": "apt-123456",
    "number": 1,
    "formattedNumber": "000-001",
    "status": "completed",
    "notes": "Installation erfolgreich abgeschlossen",
    "technician": "Max Mustermann",
    "konzernId": "KID-12345",
    "areaType": "residential",
    "lastUpdated": "2025-01-01T12:00:00.000Z"
  },
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Photos */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <FileJson className="w-5 h-5 text-blue-600" />
                <h4 className="text-lg font-semibold text-gray-800">Fotos</h4>
              </div>
              
              <div className="space-y-4">
                {/* GET /photos/apartment/{id} */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs font-bold">GET</span>
                      <code className="font-mono text-sm">/photos/apartment/{'{id}'}</code>
                    </div>
                    <span className="text-sm text-gray-500">Fotos einer Wohnung abrufen</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Gibt alle Fotos einer bestimmten Wohnung zurück.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Parameter</h5>
                    <ul className="list-disc list-inside text-sm text-gray-600 mb-2">
                      <li><code className="bg-gray-100 px-1 py-0.5 rounded">id</code> - Die ID der Wohnung</li>
                    </ul>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": [
    {
      "id": "photo-123456",
      "apartmentId": "apt-123456",
      "buildingId": "123e4567-e89b-12d3-a456-426614174000",
      "url": "https://example.com/photos/123456.jpg",
      "thumbnailUrl": "https://example.com/photos/123456_thumb.jpg",
      "fileName": "123456.jpg",
      "fileSize": 1024000,
      "mimeType": "image/jpeg",
      "createdAt": "2025-01-01T12:00:00.000Z",
      "createdBy": "user-123456",
      "location": "Wohnzimmer",
      "tags": ["Installation", "Abgeschlossen"],
      "notes": "Foto der fertigen Installation"
    },
    ...
  ],
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>

                {/* DELETE /photos/{id} */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-xs font-bold">DELETE</span>
                      <code className="font-mono text-sm">/photos/{'{id}'}</code>
                    </div>
                    <span className="text-sm text-gray-500">Foto löschen</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Löscht ein Foto.
                    </p>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Parameter</h5>
                    <ul className="list-disc list-inside text-sm text-gray-600 mb-2">
                      <li><code className="bg-gray-100 px-1 py-0.5 rounded">id</code> - Die ID des Fotos</li>
                    </ul>
                    <h5 className="text-sm font-semibold text-gray-700 mt-3 mb-1">Antwort</h5>
                    <pre className="bg-gray-50 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": true,
  "data": {
    "id": "photo-123456"
  },
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Webhooks */}
          <section>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Webhooks</h3>
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-blue-600" />
              <p className="text-gray-600">
                Webhooks ermöglichen es Ihrer Anwendung, Benachrichtigungen über Ereignisse in InstallTrack Pro zu erhalten.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="text-md font-semibold text-gray-800 mb-2">Verfügbare Ereignisse</h4>
              <ul className="list-disc list-inside text-sm text-gray-600">
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">building.created</code> - Ein neues Gebäude wurde erstellt</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">building.updated</code> - Ein Gebäude wurde aktualisiert</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">building.deleted</code> - Ein Gebäude wurde gelöscht</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">apartment.updated</code> - Eine Wohnung wurde aktualisiert</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">photo.uploaded</code> - Ein Foto wurde hochgeladen</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">photo.deleted</code> - Ein Foto wurde gelöscht</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">*</code> - Alle Ereignisse</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-800 mb-2">Webhook-Payload</h4>
              <p className="text-sm text-gray-600 mb-2">
                Jeder Webhook-Aufruf enthält einen JSON-Payload mit den folgenden Informationen:
              </p>
              <pre className="bg-gray-100 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "event": "building.created",
  "timestamp": "2025-01-01T12:00:00.000Z",
  "data": {
    // Ereignisspezifische Daten
  }
}`}
              </pre>
            </div>
          </section>

          {/* Error Handling */}
          <section>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Fehlerbehandlung</h3>
            <p className="text-gray-600 mb-4">
              Bei Fehlern gibt die API einen entsprechenden HTTP-Statuscode und eine Fehlermeldung zurück.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-800 mb-2">Beispiel für eine Fehlerantwort</h4>
              <pre className="bg-gray-100 p-2 rounded-md text-xs overflow-x-auto">
{`{
  "success": false,
  "error": "Building not found",
  "meta": {
    "version": "v1",
    "timestamp": "2025-01-01T12:00:00.000Z"
  }
}`}
              </pre>
            </div>
          </section>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};